import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, EASE_SPRING, glass, ink } from '../lib/theme';
import type { Me } from '../lib/types';
import { Avatar } from './Avatar';
import { originFrom, type Origin } from './ScaleModal';
import { IconSettings } from './icons';

/**
 * Menu do perfil, aberto pelo avatar do header. Antes o avatar fazia logout
 * direto; agora ele abre este menu, que é onde mora o acesso à gestão de
 * usuários — visível só pra admin (o servidor barra de qualquer jeito, isto é
 * conveniência de UI, não a trava).
 */
export function ProfileMenu({ me, onSignOut, onOpenSettings }: {
  me: Me;
  onSignOut: () => void;
  onOpenSettings: (origin: Origin) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const isAdmin = me.role === 'admin';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Único ponto de acesso a perfil/configurações agora (o botão
          duplicado no rodapé da sidebar saiu) — moldura própria (não só o
          `ring` do Avatar) pra dar mais presença ao espaço reservado pra
          foto no canto superior direito, pedido explícito. */}
      <button
        type="button" onClick={() => setOpen((v) => !v)} title={me.email}
        aria-haspopup="menu" aria-expanded={open}
        className="ko-ghost"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 46, height: 46, borderRadius: '50%', flex: 'none',
          border: `1.5px solid ${open ? 'rgba(255,144,112,0.55)' : ink(0.14)}`,
          background: ink(0.04), cursor: 'pointer', padding: 0,
          boxShadow: open ? '0 0 0 4px rgba(255,107,71,0.18)' : '0 0 0 0 rgba(255,107,71,0)',
          transition: 'box-shadow 0.2s, border-color 0.2s',
        }}
      ><Avatar url={me.avatar_url} email={me.email} size={38} /></button>

      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 90, width: 258,
          ...glass, borderRadius: 16, padding: 10, animation: `koSlideIn 0.2s ${EASE_SPRING}`,
        }}>
          <div style={{ padding: '6px 10px 10px', borderBottom: `1px solid ${ink(0.08)}`, marginBottom: 8 }}>
            <div style={{
              fontSize: 12.5, fontWeight: 700, color: ink(0.9),
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{me.email}</div>
            <span style={{
              display: 'inline-block', marginTop: 6, fontSize: 9.5, fontWeight: 800,
              letterSpacing: '0.07em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 99,
              background: isAdmin ? 'rgba(255,144,112,0.16)' : ink(0.09),
              color: isAdmin ? C.accent : ink(0.6),
            }}>{isAdmin ? 'administrador' : 'usuário'}</span>
          </div>

          <button
            type="button" role="menuitem" className="ko-opt"
            onClick={(e) => { setOpen(false); onOpenSettings(originFrom(e)); }}
            style={itemStyle}
          >
            <span style={{ width: 16, display: 'flex', justifyContent: 'center', flex: 'none' }}><IconSettings size={14} /></span>
            Configurações
          </button>

          {isAdmin && (
            <>
              <button
                type="button" role="menuitem" className="ko-opt"
                onClick={() => { setOpen(false); navigate('/admin/usuarios'); }}
                style={itemStyle}
              >
                <span style={{ width: 16, textAlign: 'center', flex: 'none' }}>👥</span>
                Usuários e acessos
              </button>

              <button
                type="button" role="menuitem" className="ko-opt"
                onClick={() => { setOpen(false); navigate('/admin/motivos'); }}
                style={itemStyle}
              >
                <span style={{ width: 16, textAlign: 'center', flex: 'none' }}>🏷</span>
                Motivos e exceções
              </button>
            </>
          )}

          <div style={{ height: 1, background: ink(0.07), margin: '8px 0' }} />

          <button
            type="button" role="menuitem" className="ko-opt" onClick={onSignOut}
            style={{ ...itemStyle, color: ink(0.6) }}
          >
            <span style={{ width: 16, textAlign: 'center', flex: 'none' }}>⏻</span>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none',
  background: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left',
  fontSize: 13, fontWeight: 600, color: ink(0.85), padding: '9px 10px', borderRadius: 9,
};
